import React from 'react';
import { Stack } from 'react-bootstrap';
import {
  labelStyle,
  titleStyle
} from '@/lomavisSharedUi/atoms/charts/chartTextStyles';

export interface BarChartData {
  value: number;
  name: string;
  color?: string;
  percentage?: number; // Added percentage field for country data
}

export interface LomavisBarChart2Props {
  title?: string;
  data: BarChartData[];
  style?: React.CSSProperties;
  showPercentage?: boolean; // Option to show percentage values
  sortAscending?: boolean; // Option to control sort order (true = ascending, false = descending)
  enableSorting?: boolean; // Option to enable/disable sorting completely
  centerVertically?: boolean; // Option to enable/disable vertical centering
}

/**
 * Atoms: LomavisBarChart2
 * A bar chart component that displays labels on top of the bars and percentages to the right
 * Useful for showing country data with percentages
 * Uses Bootstrap progress bars instead of ECharts
 * Supports sorting data in ascending or descending order, or disabling sorting completely
 * Can control vertical centering of content with centerVertically prop
 */
export const LomavisBarChart2: React.FC<LomavisBarChart2Props> = ({
  title,
  data,
  style,
  showPercentage = true,
  sortAscending = true,
  enableSorting = true,
  centerVertically = false
}) => {
  // Only filter out entries with value 0 if there's at least one entry with value > 0
  const hasPositiveValues = data.some((item) => item.value > 0);
  const filteredData = hasPositiveValues
    ? data.filter((item) => item.value !== 0)
    : data;
  const sortedData = enableSorting
    ? [...filteredData].sort((a, b) =>
        sortAscending ? a.value - b.value : b.value - a.value
      )
    : filteredData;

  // Find the maximum absolute value to calculate percentages for the progress bars
  const maxAbsValue = Math.max(
    ...sortedData.map((item) => Math.abs(item.value))
  );

  // Determine font size based on number of items
  const fontSize = filteredData.length > 5 ? 10 : 14;

  return (
    <div style={{ height: '100%', width: '100%', ...style }}>
      {title && (
        <div
          style={{
            ...titleStyle,
            textAlign: 'left',
            marginBottom: '15px',
            backgroundColor: '#ffffff',
            padding: '4px 8px',
            borderRadius: '4px'
          }}
        >
          {title}
        </div>
      )}

      <div className="h-400px" style={{ overflowY: 'auto' }}>
        <Stack
          gap={3}
          style={{
            height:
              centerVertically && sortedData.length <= 7 ? '100%' : 'auto',
            minHeight:
              centerVertically && sortedData.length <= 7 ? '100%' : 'auto'
          }}
          className={`d-flex flex-column ${
            centerVertically && sortedData.length <= 7
              ? 'justify-content-center'
              : ''
          }`}
        >
          {sortedData.map((item, index) => (
            <div key={`bar-${index}`} className="mb-3">
              <div
                style={{
                  ...labelStyle,
                  fontSize,
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginBottom: '4px'
                }}
                title={item.name}
              >
                {item.name}
              </div>

              <Stack
                direction="horizontal"
                gap={2}
                className="align-items-center"
              >
                <div style={{ flex: 1 }}>
                  <div
                    className="progress"
                    style={{ height: '15px', borderRadius: '5px' }}
                    title={`${item.name}: ${item.value}${
                      showPercentage && item.percentage !== undefined
                        ? ` (${item.percentage}%)`
                        : ''
                    }`}
                  >
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{
                        width: `${(Math.abs(item.value) / maxAbsValue) * 100}%`,
                        backgroundColor:
                          item.value >= 0 ? '#4B5675' : '#dc3545', // Blue for positive, red for negative
                        borderRadius: '5px'
                      }}
                      aria-valuenow={item.value}
                      aria-valuemin={0}
                      aria-valuemax={maxAbsValue}
                    >
                      {item.value < 0 && (
                        <span style={{ marginLeft: '5px' }}>-</span>
                      )}
                    </div>
                  </div>
                </div>

                {showPercentage && item.percentage !== undefined && (
                  <div
                    style={{
                      ...labelStyle,
                      fontSize,
                      width: '50px',
                      textAlign: 'right'
                    }}
                  >
                    {String(item.percentage)}%
                  </div>
                )}
              </Stack>
            </div>
          ))}
        </Stack>
      </div>
    </div>
  );
};
